import { IMAGE_MODEL_STYLE, usePersona, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Modal, Size, Switch } from "@next-platform/client-common-spicy-ui";
import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";

import { Col, Row, SpicyButton, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { FEATURE_FLAGS, SPICYCHAT_USER_PERMISSIONS } from "@/constants";
import { useAgeVerificationFlags, useFeatureFlag } from "@/hooks";

import ConversationImageLoraScale from "./ConversationImageLoraScale";
import ConversationImagePersonaToggle from "./ConversationImagePersonaToggle";
import ConversationImagePromptFields from "./ConversationImagePromptFields";
import ConversationImageStyleTabs from "./ConversationImageStyleTabs";
import useConversationImageSettings from "./useConversationImageSettings";

const MODAL_CLASS_NAMES = { title: "text-center pl-[28px]" };

type ModalConversationImageSettingsProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const checkIsQwenStyle = (modelStyle: string): boolean => modelStyle === IMAGE_MODEL_STYLE.QWEN || modelStyle === IMAGE_MODEL_STYLE.QWEN_MALE;

export default function ModalConversationImageSettings({ isOpen, setIsOpen }: ModalConversationImageSettingsProps) {
  const { t } = useTranslation();
  const { permissions } = useUserInfo();
  const isNsfwToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);
  const { hasFeatureFlag: hasFluxFeatureFlag, isLoading: isFluxFlagLoading } = useFeatureFlag(FEATURE_FLAGS.CONVERSATION_IMAGE_GENERATION_FLUX);
  const { isAgeVerificationRequired } = useAgeVerificationFlags();
  const { hasFeatureFlag: hasQwenFeatureFlag, isLoading: isQwenFlagLoading } = useFeatureFlag(FEATURE_FLAGS.CONVERSATION_IMAGE_GENERATION_QWEN);
  const { selectedPersona } = usePersona();
  const settings = useConversationImageSettings({ isAgeVerificationRequired, isNsfwToggleAllowed });

  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  const hasConversationImagesV2Permission = Boolean(permissions?.includes(SPICYCHAT_USER_PERMISSIONS.conversation_images_v2));
  const isFluxOptionShown = !isFluxFlagLoading && hasFluxFeatureFlag && hasConversationImagesV2Permission;
  const isQwenOptionShown = !isQwenFlagLoading && hasQwenFeatureFlag && hasConversationImagesV2Permission;
  const isQwenStyle = checkIsQwenStyle(settings.modelStyle);
  const hasPersonaAvatar = Boolean(selectedPersona?.avatar_url);
  const isIncludePersonaChecked = settings.isIncludePersona && hasPersonaAvatar;

  return (
    <Modal
      title={t("chat:modal.imageSettings.settingsTitle")}
      className="dark:!bg-gray-6"
      classNames={MODAL_CLASS_NAMES}
      backdrop="blur"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Col className="gap-3" dataTestId="ChatImageImageGenerationSettingsCapabilityGate">
        <Row className="leading-5">
          <Text h6 mNone>
            <Trans i18nKey="chat:modal.imageSettings.imageStyle.chooseLabel" />
          </Text>
        </Row>
        <Row>
          <ConversationImageStyleTabs
            modelStyle={settings.modelStyle}
            onModelStyleChange={settings.changeModelStyle}
            isFluxOptionShown={isFluxOptionShown}
            isQwenOptionShown={isQwenOptionShown}
          />
        </Row>
        {isNsfwToggleAllowed ? (
          <Row className="leading-5 pb-0">
            <Switch
              checked={settings.isNsfwToggled}
              onCheckedChange={settings.onNsfwModeChange}
              disabled={isAgeVerificationRequired}
              label={t("chat:modal.imageSettings.nsfwMode.label")}
              size={Size.LARGE}
            />
          </Row>
        ) : null}

        <Row>
          <Text variant={"smallest"}>
            <Trans i18nKey="chat:modal.imageSettings.nsfwMode.description" />
          </Text>
        </Row>

        {isQwenStyle ? (
          <ConversationImagePersonaToggle
            isChecked={isIncludePersonaChecked}
            hasPersonaAvatar={hasPersonaAvatar}
            onIncludePersonaChange={settings.onIncludePersonaChange}
          />
        ) : null}

        {isQwenStyle ? null : <ConversationImageLoraScale loraScale={settings.loraScale} onLoraScaleChange={settings.onLoraScaleChange} />}

        <ConversationImagePromptFields
          userPromptSuffix={settings.userPromptSuffix}
          userNegativePrompt={settings.userNegativePrompt}
          onUserPromptSuffixChange={settings.onUserPromptSuffixChange}
          onUserNegativePromptChange={settings.onUserNegativePromptChange}
        />

        {hasConversationImagesV2Permission ? (
          <Row className="leading-5 pb-0">
            <Switch
              checked={settings.isLegacyMode}
              onCheckedChange={settings.onLegacyModeChange}
              label={t("chat:modal.imageSettings.legacyMode")}
              size={Size.LARGE}
            />
          </Row>
        ) : null}
      </Col>
      <Row>
        <Text variant={"smallest"}>
          <Trans i18nKey="chat:modal.imageSettings.description" />
        </Text>
        <Col align="end">
          <SpicyButton variant="secondary_short" color="gray_light" onPress={handleClose}>
            <Trans i18nKey="chat:modal.imageSettings.cta.close" />
          </SpicyButton>
        </Col>
      </Row>
    </Modal>
  );
}
